import React, { ChangeEvent, KeyboardEvent, PureComponent } from 'react';
import LocationSuggestionsProvider from 'modules/homescreen/providers/LocationSuggestionsProvider';
import classnames from 'classnames';
import SearchNearbyProvider from 'modules/homescreen/containers/SearchNearbyProvider';
import SearchNearbyBlock from 'modules/homescreen/components/SearchNearbyBlock';
import SuggestionListDecider from 'modules/homescreen/components/SuggestionListDecider';
import LocalsOrExperiencesSelector from 'modules/homescreen/components/LocalsOrExperiencesSelector';
import { Translator } from 'Translator';
import locationTextPretty from 'modules/homescreen/helpers/locationTextPretty';
import { addFlux, AddFluxHOCProps } from 'components/wrappers/WithFlux';
import { appServices } from '../../../singletons';
import MobileWrapper, { MobileWrapperState } from 'modules/homescreen/hoc/mobileWrapper';
import { SearchMode } from 'model/Filters';
import { keywordToFilters } from 'data/keywords';
import { analyticsTrack } from 'utils/analytics';
import LocalizedNoIndexLink from 'components/LocalizedNoIndexLink';
import SearchIcon from 'components/icons/SearchIcon';
import ClickOutside from 'components/ClickOutside';

const CSS = require('./LocationSearchInput.less');

const ENTER_KEY = 13;
const DOWN_ARROW_KEY = 40;
const UP_ARROW_KEY = 38;

type LocationSearchInputProps = AddFluxHOCProps & MobileWrapperState & {
  city: string;
  country: string;
  place?: string;
  onNearbyClick?: (city: string, country: string, distanceTo?: number) => void;
  onSuggestionLockIn?: (city: string, country: string) => void;
  inNavbar?: boolean;
  isHeroInput?: boolean;
};

type LocationSearchInputState = {
  inputValue: string;
  inputListVisible: boolean;
  selectedCity: string;
  selectedCountry: string;
  selectedPlace: string;
};

class LocationSearchInput extends PureComponent<LocationSearchInputProps, LocationSearchInputState> {

  state = {
    selectedCountry: this.props.country,
    selectedCity: this.props.city,
    selectedPlace: this.props.place,
    inputValue: '',
    inputListVisible: false,
  };

  inputRef: HTMLInputElement;
  suggestionListRef: SuggestionListDecider;
  setInputRef = c => this.inputRef = c;
  setSuggestionListRef = c => this.suggestionListRef = c;

  componentDidUpdate() {
    const { country, city, place } = this.props;
    const { selectedCity, selectedCountry, selectedPlace } = this.state;
    if (selectedCity !== city || selectedCountry !== country || selectedPlace !== place) {
      this.setState({ selectedCity: city, selectedCountry: country, selectedPlace: place });
    }
  }

  getLocationText = (selectedCity, selectedCountry, selectedPlace) => {
    if (!selectedCity && !selectedCountry && !selectedPlace) {
      return Translator.translate('Homepage.SearchBlock.placeholder', {}, true, 'Where are you going?');
    }
    return locationTextPretty(selectedCity, selectedCountry, selectedPlace);
  };

  onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      inputValue: e.target.value,
    });
  };

  onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === ENTER_KEY) {
      this.inputRef.blur();
      this.selectFirstSuggestion();
    }

    if (e.keyCode === DOWN_ARROW_KEY) {
      e.preventDefault(); // prevent scroll event
      this.suggestionListRef.selectNextSuggestion();
    }
    if (e.keyCode === UP_ARROW_KEY) {
      e.preventDefault(); // prevent scroll event
      this.suggestionListRef.selectPreviousSuggestion();
    }
  };

  selectFirstSuggestion = () => {
    if (this.suggestionListRef) {
      const firstSuggestion = this.suggestionListRef.getFirstSuggestion();
      this.onSuggestionChange(firstSuggestion, true);
    }
  };

  getSelectedSuggestion = () => {
    if (this.state.selectedCity || this.state.selectedCountry || this.state.selectedPlace) {
      return {
        city: this.state.selectedCity,
        country: this.state.selectedCountry,
        place: this.state.selectedPlace,
      };
    }

    return this.suggestionListRef.getFirstSuggestion();
  };

  onClickOutside = (event) => {
    if (!this.state.inputListVisible) {
      return;
    }
    if (event?.target?.type === 'submit' && this.state.inputValue) {
      this.selectFirstSuggestion();
    } else if (this.state.inputValue || this.state.selectedCity || this.state.selectedCountry) {
      this.onSuggestionChange(this.getSelectedSuggestion(), true);
    }

    this.collapseInputList();
  };

  onSuggestionChange = (suggestion: { city: string; country: string; place: string }, lockIn = false) => {
    this.setState({
      selectedCity: suggestion.city,
      selectedCountry: suggestion.country,
      selectedPlace: suggestion.place,
    });
    if (lockIn) {
      this.lockSuggestion(suggestion);
    }
  };

  onNearbySelect = (location: { city: string; country: string; place: string }) => {
    this.onSuggestionChange(location, true);
  };

  onSearchModeChange = (mode: SearchMode) => {
    this.props.flux.actions.filters.setSearchMode(mode);
  }

  lockSuggestion = (suggestion: { city: string; country: string; place: string }) => {
    const { city, country, place } = suggestion;
    if (!city && !country && !place) {
      return null;
    }
    const oldFilters = this.props.flux.stores.FiltersStore.getFilters();
    if (city || country) {
      const newFilters = {
        ...oldFilters,
        country, city,
        activeFilters: oldFilters.activeFilters.filter(({ uid }) => ['adults', 'children', 'date.from', 'date.to'].includes(uid)),
      };
      this.props.flux.actions.filters.setFilters(newFilters);
    } else {
      const newFilters = keywordToFilters(place)(oldFilters);
      this.props.flux.actions.filters.setFilters(newFilters);
      analyticsTrack(
        { type: 'click', subject: 'keyword selected', value: { keyword: place }, valueString: place },
        { action: 'keyword selected in searchbar', label: `selected keyword: ${place}`, value: { keyword: place } }
      );
    }
    this.setState({
      inputValue: this.getLocationText(city, country, place),
      inputListVisible: false,
      selectedCountry: null,
      selectedCity: null,
      selectedPlace: null,
    });
    if (this.props.onSuggestionLockIn) {
      this.props.onSuggestionLockIn(city, country);
    }
  };

  expandInputList = () => {
    appServices.locationFunctions.getLocation();
    this.setState({
      inputListVisible: true,
      inputValue: '',
    });
  };

  collapseInputList = () => {
    this.setState({
      inputListVisible: false,
    });
  };

  render() {
    const { inputValue, selectedCountry, selectedCity, selectedPlace } = this.state;
    const locationText = this.getLocationText(selectedCity, selectedCountry, selectedPlace);
    return (
      <LocationSuggestionsProvider value={inputValue}>
        {({ suggestions, popularLocations, recentSearches, approvedLocations }) => {
          const firstCitySuggestion = selectedCity || (suggestions[0] && suggestions[0].location ?
            suggestions[0].location.city : suggestions?.[0]?.city);
          const firstCountrySuggestion = selectedCountry || (suggestions[0] && suggestions[0].location ?
            suggestions[0].location.country : suggestions?.[0]?.country);
          return (
            <div className={classnames(CSS.SearchInputContainer, { [CSS.SearchInputContainerInNavbar]: this.props.inNavbar })}>
              <ClickOutside
                onClickOutside={this.onClickOutside}
                className={classnames(CSS.SearchInputNoEdit,
                  {
                    [CSS.SearchInputEdit]: this.state.inputListVisible,
                    [CSS.SearchInputEditHero]: this.props.isHeroInput,
                  })}
              >
                <div className={classnames(CSS.SearchIconContainer, { [CSS.SearchIconContainerInNavbar]: this.props.inNavbar })}>
                  <SearchIcon width={21} height={21} className={CSS.SearchIcon} />
                </div>
                {this.props.isMobile ?
                  <LocalizedNoIndexLink
                    type="button"
                    className={classnames(CSS.SearchInput, { [CSS.SearchInputInNavbar]: this.props.inNavbar })}
                    to={{
                      pathname: '/filter/location/',
                      state: { modal: true },
                      search: '?type=all'
                    }}
                  >
                    {this.state.inputValue || locationText}
                  </LocalizedNoIndexLink> :
                  <input
                    ref={this.setInputRef}
                    data-test-id="LocationSearchInput"
                    onFocus={this.expandInputList}
                    value={this.state.inputValue}
                    className={classnames(CSS.SearchInput, { [CSS.SearchInputInNavbar]: this.props.inNavbar })}
                    onChange={this.onInputChange}
                    placeholder={locationText}
                    onKeyDown={this.onInputKeyDown}
                    data-country={firstCountrySuggestion}
                    data-city={firstCitySuggestion}
                  />
                }
                {this.state.inputListVisible &&
                  <div className={CSS.Predictions}>
                    <LocalsOrExperiencesSelector
                      mode={this.props.flux.stores.FiltersStore.searchMode}
                      onChange={this.onSearchModeChange}
                    />
                    {
                      this.props.isMobile && !this.state.inputValue && <SearchNearbyProvider>
                        {
                          ({ triggerSearch, cities, loading }) => (
                            <SearchNearbyBlock
                              cities={cities}
                              loading={loading}
                              triggerSearch={triggerSearch}
                              onSelect={this.onNearbySelect}
                            />
                          )
                        }
                      </SearchNearbyProvider>
                    }
                    <SuggestionListDecider
                      ref={this.setSuggestionListRef}
                      onSuggestionChange={this.onSuggestionChange}
                      popularLocations={popularLocations}
                      recentSearches={recentSearches}
                      suggestions={suggestions}
                      approvedLocations={approvedLocations}
                      selectedSuggestion={{ city: this.state.selectedCity, country: this.state.selectedCountry, place: this.state.selectedPlace }}
                    />
                  </div>

                }
              </ClickOutside>
            </div>
          );
        }}
      </LocationSuggestionsProvider>
    );
  }
}

export default MobileWrapper()(addFlux(LocationSearchInput));
